import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import SortableQuestionCard from './SortableQuestionCard';
import { Question } from '@synergetx/puppet-interview-types';
import { useQuestionExpand } from 'interviewEditor/QuestionExpandContext';

function DraggableSurveyQuestions() {
    const [
        { value: allQuestions },
        { error },
        { setTouched: setQuestionsTouched, setValue: setAllQuestions },
    ] = useField<Question[] | undefined>('questions');
    const { adjustPositionsForReorder } = useQuestionExpand();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Generate stable IDs for each question
    const itemIds = useMemo(
        () => allQuestions?.map((q, i) => `question-${i}-${q.type}-${q.position}`) ?? [],
        [allQuestions],
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id && allQuestions) {
                const oldIndex = itemIds.indexOf(active.id as string);
                const newIndex = itemIds.indexOf(over.id as string);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const reordered = arrayMove(allQuestions, oldIndex, newIndex);
                    setAllQuestions(reordered, true);
                    setQuestionsTouched(true);
                    // Adjust expanded positions so they follow the moved questions
                    adjustPositionsForReorder(oldIndex, newIndex);
                }
            }
        },
        [allQuestions, itemIds, setAllQuestions, setQuestionsTouched, adjustPositionsForReorder],
    );

    if (!allQuestions) return null;

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <Box
                    component="ol"
                    sx={{
                        pl: 0,
                        pr: 0,
                        m: 0,
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    {allQuestions.map((question, i) => (
                        <SortableQuestionCard
                            key={itemIds[i]}
                            id={itemIds[i]}
                            question={question}
                            position={i}
                            isLastQuestion={i === allQuestions.length - 1}
                            hasError={!!error?.[i]}
                        />
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
}

export default DraggableSurveyQuestions;
